import ImpTracker from '@/components/common/ImpTracker';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import { BannerType } from '@/constants/track';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useImpressionT2 from '@/tracks/useImpressionT2';
import { ComponentProps } from '@/types/cms';
import { CroppedImage } from '@weee-fe/module';
import classNames from 'classnames';
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import withFetch from '../../hooks/fetch';
import Container from '../Layout/container';
import BannerLineSkeleton from './skeleton';
import { handleCmsJump } from '../../tools/cms-link';
import { useRouter } from 'next/router';
import { getBannerLoopInterval } from '@/utils/get-banner-loop-interval';
import { trackAdCreative, trackAdCreativeImmediately } from '@/api/ads';

const BannerLine: React.FC<ComponentProps> = props => {
  const router = useRouter();
  let { cmsComponentKey, properties, componentPosition, isStatic, postUrl } = props;
  const [swiperIndex, setSwiperIndex] = useState(0);
  const eventKey = properties?.event_key || cmsComponentKey;
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();

  const { data, load } = withFetch({
    data: props.data,
    isStatic: isStatic ?? false,
    postUrl: postUrl
    // componentDataSourceKey
  });

  const dataList = data?.carousel || [];
  const { autoplay, loop, loop_interval } = data || {};
  const bannerLoopInterval = getBannerLoopInterval(loop_interval);

  if (load) {
    return (
      <Container cmsComponentKey={cmsComponentKey}>
        <BannerLineSkeleton />
      </Container>
    );
  }

  const onBeforeChange = swiper => {
    const { realIndex } = swiper;
    setSwiperIndex(realIndex);
  };

  if (!dataList || dataList?.length === 0) return null;

  if (dataList?.length) {
    return (
      <Container cmsComponentKey={cmsComponentKey} className={dataList?.length < 2 ? 'pb-0 relative' : 'pb-4 relative'}>
        <Swiper
          slidesPerView={'auto'}
          onSlideChangeTransitionStart={onBeforeChange}
          autoplay={
            autoplay && dataList.length > 1
              ? { delay: bannerLoopInterval, stopOnLastSlide: !loop, disableOnInteraction: false }
              : undefined
          }
          loop={dataList.length > 1 && loop}
          loopedSlides={loop ? dataList.length : undefined}>
          {dataList?.map((item, index) => {
            return (
              <SwiperSlide key={index}>
                <a
                  onClick={() => {
                    if (item.link_url) {
                      handleCmsJump(item.link_url, router);
                    }
                  }}
                  role="button"
                  tabIndex={index}
                  key={index}
                  aria-label={`Banner line item ${index}`}>
                  <ImpTracker
                    impTrackingT2={() => {
                      bannerImpression({
                        mod_nm: eventKey,
                        mod_pos: componentPosition,
                        co: {
                          banner_pos: index,
                          banner_type: BannerType.homeBannerLine,
                          url: item?.link_url,
                          banner_id: item?.id
                        }
                      });
                      if (item.trackViewUrl) {
                        trackAdCreative(item.trackViewUrl, {
                          trackViewUrl: item.trackViewUrl,
                          trackRedirectUrl: item.trackRedirectUrl,
                          trackClickUrl: item.trackClickUrl,
                          trackAddToCartUrl: item.trackAddToCartUrl
                        });
                      }
                    }}
                    clickTrackingT2={() => {
                      bannerClick({
                        mod_nm: eventKey,
                        mod_pos: componentPosition,
                        co: {
                          banner_pos: index,
                          banner_type: BannerType.homeBannerLine,
                          url: item?.link_url,
                          banner_id: item?.id
                        }
                      });
                      if (item.trackClickUrl) {
                        trackAdCreativeImmediately(item.trackClickUrl, {
                          trackViewUrl: item.trackViewUrl,
                          trackRedirectUrl: item.trackRedirectUrl,
                          trackClickUrl: item.trackClickUrl,
                          trackAddToCartUrl: item.trackAddToCartUrl
                        });
                      }
                    }}>
                    <CroppedImage
                      className="w-full h-[calc(68_*_100vw_/_375)] object-cover"
                      placeholder={<div className="w-full h-[calc(68_*_100vw_/_375)] skeletton bg-surface-200-bg" />}
                      url={item.img_url}
                      configs={{ renderWidth: 375, devicePixelRatio: 2 }}
                      wrapperClassName="w-full h-[calc(68_*_100vw_/_375)]"
                      alt={`weee banner line component ${index} image`}
                    />
                  </ImpTracker>
                </a>
              </SwiperSlide>
            );
          })}
        </Swiper>
        {/** 轮播的dot */}
        {dataList?.length > 1 && (
          <div className="absolute -translate-x-2/4 z-10 left-2/4 bottom-0">
            {new Array(dataList.length).fill(0).map((_item, index) => {
              return (
                <span
                  key={index}
                  className={twMerge(
                    classNames(
                      'inline-block w-[5px] h-[5px] bg-[#ccc] transition-[width] duration-[0.3s] ease-[ease] mr-[5px] rounded-[50%]',
                      {
                        ['bg-[#15aacb] opacity-100 w-3 transition-[width] duration-[0.3s] ease-[ease] rounded-[2.5px]']:
                          index === swiperIndex
                      }
                    )
                  )}
                />
              );
            })}
          </div>
        )}
      </Container>
    );
  }
  return null;
};

export default BannerLine;
